import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack } from '@mui/material';
import HeadTitle from '../../components/HeadTitle';
import MemberEngagement from '../member-engagement-container/index.js';
import MessagingActivity from '../messaging-activity-container/index.js';

/**
 * Engagement — unified tab combining the former "Member Engagement" and
 * "Messaging Activity" pages under a single sidebar entry.
 *
 * A "Subscribed | All" segmented control switches between the two views:
 *   - Subscribed → Member Engagement (paid members' response quality)
 *   - All        → Messaging Activity (every member's outbound/inbound volume)
 *
 * The active view is kept in the `?view=` query param so it survives a refresh
 * and is shareable/deep-linkable.
 */
const VIEWS = [
  {
    key: 'subscribed',
    label: 'Subscribed',
    hint: 'Paid members’ response quality & activity',
    icon: 'tabler:crown',
  },
  {
    key: 'all',
    label: 'All',
    hint: 'Per-member outbound & inbound messaging volume',
    icon: 'tabler:messages',
  },
];

const Engagement = () => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'all' ? 'all' : 'subscribed';
  const activeIndex = VIEWS.findIndex((v) => v.key === view);
  const active = VIEWS[activeIndex];

  const setView = (v) => {
    const next = new URLSearchParams(params);
    next.set('view', v);
    setParams(next, { replace: true });
  };

  return (
    <div className="engagement-scope">
      <style>{`
        .engagement-scope { --eng-accent: #E8392E; }

        .engagement-scope .eng-toggle {
          position: relative;
          display: inline-flex;
          padding: 5px;
          background: #f1f2f4;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          box-shadow: inset 0 1px 2px rgba(16,24,40,.04);
        }
        /* Sliding highlight pill */
        .engagement-scope .eng-toggle .slider {
          position: absolute;
          top: 5px; bottom: 5px; left: 5px;
          width: calc(50% - 5px);
          border-radius: 10px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(16,24,40,.12), 0 1px 2px rgba(16,24,40,.06);
          transition: transform .22s cubic-bezier(.4,0,.2,1);
        }
        .engagement-scope .eng-toggle[data-active="1"] .slider { transform: translateX(100%); }

        .engagement-scope .eng-toggle .opt {
          position: relative;
          z-index: 1;
          border: 0;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          min-width: 150px;
          border-radius: 10px;
          text-align: left;
          transition: color .18s;
        }
        .engagement-scope .eng-toggle .opt iconify-icon { font-size: 20px; flex: none; }
        .engagement-scope .eng-toggle .opt .txt { display: flex; flex-direction: column; line-height: 1.15; }
        .engagement-scope .eng-toggle .opt .txt b { font-size: 14px; font-weight: 700; }
        .engagement-scope .eng-toggle .opt .txt small { font-size: 11px; color: #9ca3af; font-weight: 500; }
        .engagement-scope .eng-toggle .opt:hover { color: #374151; }
        .engagement-scope .eng-toggle .opt.on { color: var(--eng-accent); }
        .engagement-scope .eng-toggle .opt.on .txt small { color: #b16460; }

        @media (max-width: 640px) {
          .engagement-scope .eng-toggle { width: 100%; }
          .engagement-scope .eng-toggle .opt { flex: 1; min-width: 0; justify-content: center; }
          .engagement-scope .eng-toggle .opt .txt small { display: none; }
        }
      `}</style>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 2.5 }}
      >
        <HeadTitle
          imageSrc="/assets/images/admin-panel/analytics.png"
          text="Engagement"
          description={active.hint}
        />

        <div className="eng-toggle" data-active={activeIndex} role="tablist" aria-label="Engagement view">
          <span className="slider" aria-hidden="true" />
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={`opt ${view === v.key ? 'on' : ''}`}
              onClick={() => setView(v.key)}
            >
              <iconify-icon icon={v.icon} />
              <span className="txt">
                <b>{v.label}</b>
                <small>{v.label === 'Subscribed' ? 'Paid members' : 'Everyone'}</small>
              </span>
            </button>
          ))}
        </div>
      </Stack>

      {view === 'subscribed' ? <MemberEngagement /> : <MessagingActivity embedded />}
    </div>
  );
};

export default Engagement;
